import {Component, Input, OnInit} from '@angular/core';
import {AbstractControl, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {NumberingMode} from '@domain/machine/numbering-mode.enum';
import {AssertionUtils, BaseComponent, EnumUtils} from '@vdw/angular-component-library';
import {debounceTime, startWith, takeUntil} from 'rxjs';
import {ToolCavitiesForm} from '../tool.form';

@Component({
  selector: 'app-cavities',
  templateUrl: './cavities.component.html'
})
export class CavitiesComponent extends BaseComponent implements OnInit {
  @Input() public form: ToolCavitiesForm;

  public numberingModes = EnumUtils.getEnumValues(NumberingMode);

  private cavitesValidator = this.getCavitiesValidator();
  private usedCavitiesValidator = this.getUsedCavitiesValidator();
  private cavitiesAcrossValidator = this.getCavitiesAcrossValidator();
  private cavitiesDownValidator = this.getCavitiesDownValidator();

  public constructor() {
    super();
  }

  public ngOnInit(): void {
    this.setUsedCavities();

    this.form.controls.cavities.valueChanges.pipe(takeUntil(this.unSubscribeOnViewDestroy), startWith(this.form.controls.cavities.value)).subscribe((cavities: number) => {
      if (!AssertionUtils.isNullOrUndefined(cavities) && cavities > 1) {
        this.form.controls.cavities.setValidators([Validators.min(1), this.cavitesValidator]);
        this.form.controls.usedCavities.setValidators([Validators.min(1), this.usedCavitiesValidator]);
        this.form.controls.cavitiesAcross.setValidators([Validators.min(0), this.cavitiesAcrossValidator]);
        this.form.controls.cavitiesDown.setValidators([Validators.min(0), this.cavitiesDownValidator]);
      } else {
        this.form.controls.cavities.removeValidators(this.cavitesValidator);
        this.form.controls.usedCavities.removeValidators(this.usedCavitiesValidator);
        this.form.controls.cavitiesAcross.removeValidators(this.cavitiesAcrossValidator);
        this.form.controls.cavitiesDown.removeValidators(this.cavitiesDownValidator);
      }
    });
  }

  public isCavitiesGreaterThanOne(): boolean {
    return this.form.value.cavities > 1;
  }

  private setUsedCavities(): void {
    this.form.controls.cavities.valueChanges.pipe(debounceTime(500), takeUntil(this.unSubscribeOnViewDestroy)).subscribe((cavities: number) => {
      const usedCavities = this.form.controls.usedCavities;
      if (AssertionUtils.isNullOrUndefined(usedCavities.value) && usedCavities.untouched && cavities > 1) {
        usedCavities.setValue(cavities);
      }

      if (cavities < 1 && usedCavities.value === 0) {
        usedCavities.setValue(null);
      }
    });
  }

  private getCavitiesValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      const usedCavities = this.form?.value.usedCavities;
      const cavitiesAcross = this.form?.value.cavitiesAcross;
      const cavitiesDown = this.form?.value.cavitiesDown;
      const expectedProductOfCavities = cavitiesAcross * cavitiesDown;

      if (usedCavities <= value) {
        this.setErrorAsNull('usedCavities', 'higherThanCavities');
      }

      if (cavitiesAcross && cavitiesDown && value === expectedProductOfCavities) {
        this.setErrorAsNull('cavitiesAcross', 'equalToCavities');
        this.setErrorAsNull('cavitiesDown', 'equalToCavities');
      }

      if (cavitiesAcross && cavitiesDown && value !== expectedProductOfCavities) {
        return {equalToCavitiesAcrossAndDown: true};
      }

      if (usedCavities && value > 1 && value < usedCavities) {
        return {lowerThanUsedCavities: true};
      }

      return null;
    };
  }

  private getUsedCavitiesValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      const cavities = this.form?.value.cavities;

      if (value > 1 && cavities >= value) {
        this.setErrorAsNull('cavities', 'lowerThanUsedCavities');
      }

      if (cavities && value > cavities) {
        return {higherThanCavities: true};
      }

      return null;
    };
  }

  private getCavitiesAcrossValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      const cavities = this.form?.value.cavities;
      const cavitiesDown = this.form?.value.cavitiesDown;
      const expectedProductOfCavities = value * cavitiesDown;

      if (!AssertionUtils.isNullOrUndefined(value) && !AssertionUtils.isNullOrUndefined(cavitiesDown) && cavities !== expectedProductOfCavities) {
        return {equalToCavities: true};
      } else {
        this.setErrorAsNull('cavities', 'equalToCavitiesAcrossAndDown');
        this.setErrorAsNull('cavitiesDown', 'equalToCavities');
      }

      if (!AssertionUtils.isNullOrUndefined(value) && AssertionUtils.isNullOrUndefined(cavitiesDown)) {
        this.setError('cavitiesDown', {equalToCavities: true});
      } else if (AssertionUtils.isNullOrUndefined(value) && !AssertionUtils.isNullOrUndefined(cavitiesDown)) {
        return {equalToCavities: true};
      } else if (AssertionUtils.isNullOrUndefined(value) && AssertionUtils.isNullOrUndefined(cavitiesDown)) {
        this.setErrorAsNull('cavitiesDown', 'equalToCavities');
      }

      return null;
    };
  }

  private getCavitiesDownValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      const cavities = this.form?.value.cavities;
      const cavitiesAcross = this.form?.value.cavitiesAcross;
      const expectedProductOfCavities = value * cavitiesAcross;

      if (!AssertionUtils.isNullOrUndefined(value) && !AssertionUtils.isNullOrUndefined(cavitiesAcross) && cavities !== expectedProductOfCavities) {
        return {equalToCavities: true};
      } else {
        this.setErrorAsNull('cavities', 'equalToCavitiesAcrossAndDown');
        this.setErrorAsNull('cavitiesAcross', 'equalToCavities');
      }

      if (!AssertionUtils.isNullOrUndefined(value) && AssertionUtils.isNullOrUndefined(cavitiesAcross)) {
        this.setError('cavitiesAcross', {equalToCavities: true});
      } else if (AssertionUtils.isNullOrUndefined(value) && !AssertionUtils.isNullOrUndefined(cavitiesAcross)) {
        return {equalToCavities: true};
      } else if (AssertionUtils.isNullOrUndefined(value) && AssertionUtils.isNullOrUndefined(cavitiesAcross)) {
        this.setErrorAsNull('cavitiesAcross', 'equalToCavities');
      }

      return null;
    };
  }

  private setErrorAsNull(formControlName: string, error: string): void {
    if (this.form?.controls[formControlName].hasError(error)) {
      this.form.controls[formControlName].setErrors(null);
    }
  }

  private setError(formControlName: string, error: ValidationErrors): void {
    this.form.controls[formControlName].setErrors(error);
  }
}
